import { useState, useRef } from "react";
import { Images, Plus, Trash2, Upload, ArrowLeftRight, Save, ZoomIn, ZoomOut, MoveVertical, RotateCcw } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import PhotoAnnotator from "@/components/lab/PhotoAnnotator";

const uid = () => Math.random().toString(36).slice(2, 10);

export const ANGLES = [
  { key: "frontale", label: "Frontale" },
  { key: "laterale", label: "Laterale" },
  { key: "dietro", label: "Dietro" },
];

export const emptyFoto = () => ({
  sections: [0, 1, 2, 3].map((i) => ({ id: uid(), label: `Mese ${i}`, date: "", photos: {}, view: {} })),
});

const todayISO = () => new Date().toISOString().slice(0, 10);
const DEFAULT_VIEW = { zoom: 1, y: 0 };

const compressImage = (file, maxSize = 1100, quality = 0.72) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      let { width, height } = img;
      const scale = Math.min(1, maxSize / Math.max(width, height));
      width = Math.round(width * scale);
      height = Math.round(height * scale);
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      canvas.getContext("2d").drawImage(img, 0, 0, width, height);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL("image/jpeg", quality));
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("img")); };
    img.src = url;
  });

function PhotoFrame({ src, onUpload, onClear, view, onView, angleLabel, testid, annotatable, anno, onAnno, annoKey }) {
  const ref = useRef(null);
  const zoom = view?.zoom ?? 1;
  const y = view?.y ?? 0;

  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Seleziona un'immagine"); return; }
    try {
      const dataUrl = await compressImage(file);
      onUpload(dataUrl);
    } catch { toast.error("Errore caricamento immagine"); }
    if (ref.current) ref.current.value = "";
  };

  return (
    <div>
      <div className="relative overflow-hidden rounded-xl border border-white/10 bg-black/30" style={{ aspectRatio: "9 / 16" }} data-testid={testid}>
        {src ? (
          <img src={src} alt={angleLabel} className="h-full w-full object-cover transition-transform duration-150" style={{ transform: `translateY(${y}%) scale(${zoom})` }} />
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center gap-2 text-muted-foreground">
            <Images className="h-8 w-8 opacity-40" />
            <span className="text-[11px]">{angleLabel}</span>
            <span className="text-[10px] opacity-60">9:16</span>
          </div>
        )}
        {annotatable && src && (
          <PhotoAnnotator key={annoKey} value={anno || []} onChange={onAnno} testid={`${testid}-anno`} />
        )}
        <div className="absolute inset-x-0 bottom-0 z-30 flex items-center justify-between gap-1 bg-gradient-to-t from-black/80 to-transparent p-2">
          <button onClick={() => ref.current?.click()} className="inline-flex items-center gap-1 rounded-md bg-primary/90 px-2 py-1 text-[11px] font-semibold text-primary-foreground hover:bg-primary" data-testid={`${testid}-upload`}>
            <Upload className="h-3 w-3" /> {src ? "Cambia" : "Carica"}
          </button>
          {src && (
            <button onClick={onClear} className="inline-flex items-center gap-1 rounded-md bg-black/60 px-2 py-1 text-[11px] font-semibold text-white/90 hover:bg-destructive hover:text-white" data-testid={`${testid}-clear`}>
              <Trash2 className="h-3 w-3" /> Elimina
            </button>
          )}
        </div>
        <input ref={ref} type="file" accept="image/*" className="hidden" onChange={handle} data-testid={`${testid}-input`} />
      </div>

      {src && onView && (
        <div className="mt-2 space-y-1.5 rounded-lg border border-white/5 bg-black/20 px-2.5 py-2">
          <div className="flex items-center gap-2">
            <ZoomOut className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <input type="range" min={1} max={3} step={0.02} value={zoom} onChange={(e) => onView({ zoom: Number(e.target.value), y })} className="w-full accent-[hsl(var(--primary))]" data-testid={`${testid}-zoom`} />
            <ZoomIn className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          </div>
          <div className="flex items-center gap-2">
            <MoveVertical className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <input type="range" min={-40} max={40} step={1} value={y} onChange={(e) => onView({ zoom, y: Number(e.target.value) })} className="w-full accent-[hsl(var(--primary))]" data-testid={`${testid}-posy`} />
            <button onClick={() => onView({ ...DEFAULT_VIEW })} className="shrink-0 text-muted-foreground hover:text-foreground" title="Reimposta" data-testid={`${testid}-reset`}><RotateCcw className="h-3.5 w-3.5" /></button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function FotoTab({ foto, setFoto, onSave }) {
  const F = foto && foto.sections ? foto : emptyFoto();
  const sections = F.sections;
  const [angleIdx, setAngleIdx] = useState(0);
  const angle = ANGLES[angleIdx].key;
  const angleLabel = ANGLES[angleIdx].label;

  const [primaId, setPrimaId] = useState(sections[0]?.id);
  const [dopoId, setDopoId] = useState(sections[sections.length - 1]?.id);

  const prima = sections.find((s) => s.id === primaId) || sections[0];
  const dopo = sections.find((s) => s.id === dopoId) || sections[sections.length - 1];

  const setPhoto = (sectionId, dataUrl) =>
    setFoto({
      ...F,
      sections: sections.map((s) =>
        s.id === sectionId ? { ...s, photos: { ...s.photos, [angle]: dataUrl }, date: s.date || todayISO() } : s
      ),
    });

  const clearPhoto = (sectionId) =>
    setFoto({
      ...F,
      sections: sections.map((s) => {
        if (s.id !== sectionId) return s;
        const photos = { ...s.photos };
        delete photos[angle];
        const view = { ...(s.view || {}) };
        delete view[angle];
        const anno = { ...(s.anno || {}) };
        delete anno[angle];
        return { ...s, photos, view, anno };
      }),
    });

  const setView = (sectionId, patch) =>
    setFoto({
      ...F,
      sections: sections.map((s) =>
        s.id === sectionId
          ? { ...s, view: { ...(s.view || {}), [angle]: { ...DEFAULT_VIEW, ...(s.view?.[angle] || {}), ...patch } } }
          : s
      ),
    });

  const setAnno = (sectionId, shapes) =>
    setFoto({
      ...F,
      sections: sections.map((s) =>
        s.id === sectionId ? { ...s, anno: { ...(s.anno || {}), [angle]: shapes } } : s
      ),
    });

  const setField = (sectionId, field, val) =>
    setFoto({ ...F, sections: sections.map((s) => (s.id === sectionId ? { ...s, [field]: val } : s)) });

  const addSection = () =>
    setFoto({ ...F, sections: [...sections, { id: uid(), label: `Mese ${sections.length}`, date: "", photos: {}, view: {} }] });

  const removeSection = (sectionId) => {
    if (sections.length <= 1) { toast.error("Deve restare almeno una sezione"); return; }
    setFoto({ ...F, sections: sections.filter((s) => s.id !== sectionId) });
  };

  return (
    <div className="space-y-6" data-testid="tab-foto">
      {/* Angle slider */}
      <section className="panel p-5">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h2 className="flex items-center gap-2 font-display text-lg font-bold">
            <Images className="h-5 w-5 text-primary" /> Foto — Confronto Prima / Dopo
          </h2>
          <button onClick={() => onSave && onSave()} className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5" data-testid="save-foto-btn">
            <Save className="h-4 w-4" /> Salva foto
          </button>
        </div>

        <div className="mx-auto max-w-md">
          <input
            type="range" min={0} max={2} step={1} value={angleIdx}
            onChange={(e) => setAngleIdx(Number(e.target.value))}
            className="w-full accent-[hsl(var(--primary))]"
            data-testid="angle-slider"
          />
          <div className="mt-1 flex justify-between">
            {ANGLES.map((a, i) => (
              <button key={a.key} onClick={() => setAngleIdx(i)} data-testid={`angle-${a.key}`}
                className={`text-xs font-semibold transition-colors ${i === angleIdx ? "text-primary" : "text-muted-foreground hover:text-foreground"}`}>
                {a.label}
              </button>
            ))}
          </div>
          <p className="mt-2 text-center text-[11px] text-muted-foreground">Usa i cursori sotto ogni foto per zoomare e allineare le immagini a confronto.</p>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-4 sm:gap-8">
          {[["Prima", prima, primaId, setPrimaId, "prima"], ["Dopo", dopo, dopoId, setDopoId, "dopo"]].map(([tag, sec, selId, setSel, key]) => (
            <div key={key} className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <span className="rounded-md bg-white/5 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-primary">{tag}</span>
                {key === "prima" && <ArrowLeftRight className="hidden h-4 w-4 text-muted-foreground sm:block" />}
              </div>
              <Select value={selId} onValueChange={setSel}>
                <SelectTrigger className="lab-input h-auto" data-testid={`compare-${key}-select`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {sections.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <PhotoFrame
                src={sec?.photos?.[angle]}
                view={sec?.view?.[angle]}
                onView={(patch) => setView(sec.id, patch)}
                angleLabel={angleLabel}
                onUpload={(d) => setPhoto(sec.id, d)}
                onClear={() => clearPhoto(sec.id)}
                testid={`compare-${key}-frame`}
                annotatable
                anno={sec?.anno?.[angle]}
                onAnno={(shapes) => setAnno(sec.id, shapes)}
                annoKey={`${sec?.id}-${angle}`}
              />
              <div className="text-center text-[11px] text-muted-foreground">
                {sec?.date ? `Caricata il ${new Date(sec.date).toLocaleDateString("it-IT")}` : "Nessuna data"}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Month sections grid */}
      <section className="panel p-5">
        <div className="mb-4 flex items-center justify-between gap-3">
          <h3 className="font-display text-base font-bold">Sezioni Temporali <span className="text-xs font-normal text-muted-foreground">(vista: {angleLabel})</span></h3>
          <button onClick={addSection} className="inline-flex items-center gap-1.5 rounded-lg border border-primary/40 bg-primary/10 px-3 py-2 text-sm font-medium text-primary transition-colors hover:bg-primary/20" data-testid="add-foto-section-btn">
            <Plus className="h-4 w-4" /> Aggiungi
          </button>
        </div>

        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {sections.map((s, i) => (
            <div key={s.id} className="space-y-2" data-testid={`foto-section-${i}`}>
              <div className="flex items-center gap-1">
                <input value={s.label} onChange={(e) => setField(s.id, "label", e.target.value)} className="min-w-0 flex-1 rounded bg-black/30 px-2 py-1 text-sm font-semibold outline-none focus:ring-1 focus:ring-primary/40" data-testid={`foto-label-${i}`} />
                <button onClick={() => removeSection(s.id)} className="shrink-0 rounded-md border border-white/10 p-1.5 text-muted-foreground hover:text-destructive" data-testid={`remove-foto-section-${i}`}><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
              <PhotoFrame
                src={s.photos?.[angle]}
                view={s.view?.[angle]}
                onView={(patch) => setView(s.id, patch)}
                angleLabel={angleLabel}
                onUpload={(d) => setPhoto(s.id, d)}
                onClear={() => clearPhoto(s.id)}
                testid={`foto-frame-${i}`}
              />
              <input type="date" value={s.date || ""} onChange={(e) => setField(s.id, "date", e.target.value)} className="w-full rounded bg-black/30 px-2 py-1.5 text-xs text-muted-foreground outline-none focus:ring-1 focus:ring-primary/40" data-testid={`foto-date-${i}`} />
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
